import { useSyncExternalStore } from "react"

export type TargetLivePosition = { lat: number; lng: number }

const positions = new Map<string, TargetLivePosition>()
const perTargetListeners = new Map<string, Set<() => void>>()
const globalListeners = new Set<() => void>()
let livePositionVersion = 0

const emitForTarget = (targetId: string) => {
  perTargetListeners.get(targetId)?.forEach((l) => l())
}

const emitGlobal = () => {
  livePositionVersion += 1
  for (const listener of globalListeners) {
    listener()
  }
}

export const setTargetLivePosition = (
  targetId: string,
  position: TargetLivePosition,
): void => {
  const prev = positions.get(targetId)
  if (prev && prev.lat === position.lat && prev.lng === position.lng) return
  positions.set(targetId, { lat: position.lat, lng: position.lng })
  emitForTarget(targetId)
  emitGlobal()
}

export const removeTargetLivePosition = (targetId: string): void => {
  if (!positions.has(targetId)) return
  positions.delete(targetId)
  emitForTarget(targetId)
  perTargetListeners.delete(targetId)
  emitGlobal()
}

export const getTargetLivePosition = (
  targetId: string | null | undefined,
): TargetLivePosition | null =>
  (targetId ? positions.get(targetId) : undefined) ?? null

export const getTargetLivePositionVersion = (): number => livePositionVersion

export const subscribeTargetLivePositions = (
  listener: () => void,
): (() => void) => {
  globalListeners.add(listener)
  return () => {
    globalListeners.delete(listener)
  }
}

const subscribeToTargetPosition = (
  targetId: string,
  listener: () => void,
): (() => void) => {
  let set = perTargetListeners.get(targetId)
  if (!set) {
    set = new Set()
    perTargetListeners.set(targetId, set)
  }
  set.add(listener)
  return () => {
    const s = perTargetListeners.get(targetId)
    if (!s) return
    s.delete(listener)
    if (s.size === 0) perTargetListeners.delete(targetId)
  }
}

/**
 * Subscribe to live position updates for a single target (per-ID subscription).
 * Only the component for the moved target re-renders — other markers are unaffected.
 * Priority on the map: drag preview > live position > target.lat/lng from React Query.
 */
export const useTargetLivePosition = (
  targetId: string | null | undefined,
): TargetLivePosition | null =>
  useSyncExternalStore(
    (listener) =>
      targetId ? subscribeToTargetPosition(targetId, listener) : () => {},
    () => getTargetLivePosition(targetId),
    () => null,
  )

/**
 * Re-render when any target live position changes (for map lines / batch consumers).
 */
export const useAnyTargetLivePositionVersion = (): number =>
  useSyncExternalStore(
    subscribeTargetLivePositions,
    getTargetLivePositionVersion,
    () => 0,
  )
